// Appointment — services list: cards with weekly summary, active toggle,
// edit (full editor incl. availability + closures) and soft delete.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Plus, Search, X, Clock, Users, Pencil, Trash2, CalendarClock, CalendarOff,
  MoreVertical, Pause, Play, Eye,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { appointmentsService, type ApptService } from '@/services/appointments.service';
import { ActionMenu } from './Popover';
import { BookingLinkButton } from './BookingLinkButton';
import { useLabels, useCompanyCurrency } from '@/contexts/WorkspaceContext';

const ApptServicesPage = () => {
  const companyCurrency = useCompanyCurrency();
  const t = useLabels();
  const navigate = useNavigate();
  const [items, setItems] = useState<ApptService[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [deleteFor, setDeleteFor] = useState<ApptService | null>(null);

  const load = () => {
    setLoading(true);
    appointmentsService.listServices()
      .then((r) => setItems(r.items ?? []))
      .catch(() => { /* interceptor toasts */ })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [items, query]);

  const toggleActive = async (s: ApptService) => {
    // optimistic — the editor round-trips the full row, the toggle shouldn't
    setItems((arr) => arr.map((x) => (x.serviceUid === s.serviceUid ? { ...x, isActive: !s.isActive } : x)));
    try {
      await appointmentsService.updateService(s.serviceUid, {
        name: s.name, description: s.description ?? '', imageUrl: s.imageUrl ?? undefined,
        durationMinutes: s.durationMinutes, price: s.price ?? undefined, currency: s.currency ?? undefined,
        capacityPerSlot: s.capacityPerSlot, maxAdvanceDays: s.maxAdvanceDays, minLeadMinutes: s.minLeadMinutes,
        bufferBeforeMin: s.bufferBeforeMin, bufferAfterMin: s.bufferAfterMin, isActive: !s.isActive,
      });
    } catch {
      load();
    }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    try {
      await appointmentsService.deleteService(deleteFor.serviceUid);
      toast.success('Service deleted');
      load();
    } catch { /* interceptor toasts */ }
    setDeleteFor(null);
  };

  return (
    <>
      <Topbar
        title={t('services')}
        breadcrumb={`${t('appointments')} / ${t('services')}`}
        description="What customers can book."
        right={<BookingLinkButton />}
      />
      <div className="w-full px-7 py-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-1.5 bg-surface border border-border rounded-lg pl-2.5 pr-1 h-8 w-[300px]">
            <Search size={14} className="text-text-muted shrink-0" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search services…" className="border-none bg-transparent text-[12.5px] flex-1 min-w-0 text-text-primary placeholder:text-text-muted outline-none" />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="border-none bg-transparent cursor-pointer text-text-muted hover:text-text-primary p-0.5 shrink-0"><X size={13} /></button>
            )}
          </div>
          <Button className="ml-auto" size="sm" iconLeft={<Plus size={15} />} onClick={() => navigate('/appointments/services/new')}>
            Add {t('services', 'singular').toLowerCase()}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-text-muted"><Loader2 size={17} className="animate-spin" /> Loading services…</div>
        ) : list.length === 0 ? (
          <div className="flex justify-center pt-[12vh]">
            <button
              type="button"
              onClick={() => navigate('/appointments/services/new')}
              className="w-full max-w-[620px] rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-surface/50 hover:bg-primary-soft/30 transition-colors cursor-pointer flex items-center gap-4 px-5 py-4 text-left group"
            >
              <span className="w-11 h-11 rounded-xl bg-primary-soft text-primary flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <CalendarClock size={21} strokeWidth={1.6} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-text-primary">{query ? `No ${t('services').toLowerCase()} match your search` : `No ${t('services').toLowerCase()} yet`}</span>
                <span className="block text-[12px] text-text-muted leading-relaxed mt-0.5">
                  {query
                    ? `Try a different keyword, or add a new ${t('services', 'singular').toLowerCase()}.`
                    : `Add your first ${t('services', 'singular').toLowerCase()} — it becomes bookable on the calendar and your public booking link.`}
                </span>
              </span>
              <span className="w-8 h-8 rounded-full bg-primary-soft text-primary group-hover:bg-primary group-hover:text-on-primary transition-colors flex items-center justify-center shrink-0">
                <Plus size={16} />
              </span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {list.map((s) => (
              <div
                key={s.serviceUid}
                onClick={() => navigate(`/appointments/services/${s.serviceUid}`)}
                className={clsx('rounded-2xl border border-border bg-surface overflow-hidden flex transition-all hover:shadow-elevated cursor-pointer', !s.isActive && 'opacity-75')}
              >
                {/* image — full card height on the left */}
                {s.imageUrl ? (
                  <img src={s.imageUrl} alt="" className="w-[132px] self-stretch object-cover object-[center_28%] shrink-0" />
                ) : (
                  <div className="w-[132px] self-stretch bg-gradient-to-b from-primary/15 to-primary/5 text-primary flex items-center justify-center shrink-0">
                    <CalendarClock size={30} strokeWidth={1.3} />
                  </div>
                )}

                {/* content */}
                <div className="min-w-0 flex-1 p-3.5 flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={clsx(
                          'w-2 h-2 rounded-full shrink-0',
                          s.isActive ? 'bg-emerald-500' : 'bg-slate-400',
                        )} title={s.isActive ? 'Active' : 'Paused'} />
                        <span className="text-[14.5px] font-bold text-text-primary truncate">{s.name}</span>
                      </div>
                      <div className="text-[12px] text-text-muted line-clamp-2 mt-1">{s.description || 'No description'}</div>
                    </div>
                    <ActionMenu
                      trigger={
                        <button type="button" className="w-8 h-8 rounded-lg bg-transparent hover:bg-surface-alt text-text-muted hover:text-text-primary border-none cursor-pointer flex items-center justify-center transition-colors shrink-0 -mr-1">
                          <MoreVertical size={15} />
                        </button>
                      }
                      items={[
                        { label: 'View details', icon: <Eye size={14} />, onSelect: () => navigate(`/appointments/services/${s.serviceUid}`) },
                        { label: 'Edit service', icon: <Pencil size={14} />, onSelect: () => navigate(`/appointments/services/${s.serviceUid}/edit`) },
                        { label: 'Closed dates', icon: <CalendarOff size={14} />, onSelect: () => navigate(`/appointments/services/${s.serviceUid}/edit`) },
                        {
                          label: s.isActive ? 'Pause bookings' : 'Accept bookings',
                          icon: s.isActive ? <Pause size={14} /> : <Play size={14} />,
                          onSelect: () => void toggleActive(s),
                        },
                        { label: 'Delete service', icon: <Trash2 size={14} />, danger: true, separatorAbove: true, onSelect: () => setDeleteFor(s) },
                      ]}
                    />
                  </div>

                  {/* stats */}
                  <div className="flex flex-wrap gap-1.5 text-[11.5px] pt-2 border-t border-border mt-auto">
                    <span className="inline-flex items-center gap-1 bg-surface-alt rounded-full px-2.5 py-1 text-text-primary"><Clock size={11} /> {s.durationMinutes} min</span>
                    {s.price != null && (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-primary-soft text-primary font-semibold">{s.currency || companyCurrency} {s.price}</span>
                    )}
                    <span className="inline-flex items-center gap-1 bg-surface-alt rounded-full px-2.5 py-1 text-text-primary"><Users size={11} /> {s.capacityPerSlot}/slot</span>
                    {(s.hours ?? []).filter((h) => h.isOpen).length > 0 && (
                      <span className="bg-surface-alt rounded-full px-2.5 py-1 text-text-muted">{(s.hours ?? []).filter((h) => h.isOpen).length} days/week</span>
                    )}
                    <span className="bg-surface-alt rounded-full px-2.5 py-1 text-text-muted">{s.bookingCount ?? 0} booking{(s.bookingCount ?? 0) === 1 ? '' : 's'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteFor}
        title={`Delete "${deleteFor?.name ?? ''}"?`}
        message="The service stops accepting bookings everywhere (calendar and the public booking link). Existing bookings are kept. This can't be undone."
        danger
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteFor(null)}
      />
    </>
  );
};

export default ApptServicesPage;
